/**
 * CrushSkillEffect — Grit grapple-path crushing hold.
 *
 * Tendrils constrict a target for 3 seconds, dealing 8% of their max HP
 * per second (24% total over 3 ticks). Player is immobilized during the
 * hold. Clinch passive applies 40% DR to the player if unlocked.
 *
 * Uses a custom state machine (idle -> windup -> active -> recovery ->
 * cooldown) for the same reasons as GrappleSkillEffect — early hold
 * cancellation on target death and fizzle mechanics.
 *
 * Damage ticks fire every tickIntervalMs during the active phase. Each
 * tick records the damage dealt (target.maxHp * resolvedDamageRate) for
 * the integration layer to apply. The skill signals damage intent; the
 * game layer executes.
 *
 * Pure logic — no Phaser dependencies.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see grit-spec.md § Crush
 */

import type { SkillEffect } from '../../SkillEffect.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface CrushDeps {
  readonly resolver: {
    getAdditiveEffect(skillId: string, key: string): number;
    getMaxEffect(skillId: string, key: string): number | undefined;
    hasEffect(skillId: string, key: string): boolean;
  };
  readonly statusEffects: {
    apply(statusId: string, ...args: unknown[]): void;
    remove(statusId: string, ...args: unknown[]): void;
    has(statusId: string, ...args: unknown[]): boolean;
  };
  readonly targeting: {
    hasTargetInRange(rangeTiles: number): boolean;
    shouldFizzle(): boolean;
    isTargetAlive(): boolean;
    getTargetMaxHp(): number;
  };
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface CrushConfig {
  readonly windupMs: number;
  readonly holdDurationMs: number;
  readonly recoveryMs: number;
  readonly cooldownMs: number;
  readonly baseRangeTiles: number;
  readonly baseDamagePerSecond: number;
  readonly tickIntervalMs: number;
  readonly damageReduction: number;
}

const DEFAULT_CONFIG: CrushConfig = {
  windupMs: 100,
  holdDurationMs: 3000,           // 3-second squeeze
  recoveryMs: 200,
  cooldownMs: 2000,               // long cooldown tier
  baseRangeTiles: 0.8,            // close melee range
  baseDamagePerSecond: 0.08,      // 8% max HP per second
  tickIntervalMs: 1000,           // damage ticks every second
  damageReduction: 0.4,           // Clinch passive DR
};

// ---------------------------------------------------------------------------
// Phase type
// ---------------------------------------------------------------------------

export type CrushPhase = 'idle' | 'windup' | 'active' | 'recovery' | 'cooldown';

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class CrushSkillEffect implements SkillEffect {
  readonly skillId = 'crush';
  readonly type = 'active' as const;
  readonly activationMode = 'instant' as const;

  private readonly deps: CrushDeps;
  private readonly config: CrushConfig;

  private _phase: CrushPhase = 'idle';
  private _phaseTimer = 0;
  private _complete = false;
  private _fizzled = false;

  private _holdActive = false;
  private _movementLocked = false;
  private _resolvedRange = 0;
  private _resolvedDamageRate = 0;
  private _tickTimer = 0;
  private _ticksFired = 0;
  private _damageTicks: number[] = [];
  private _totalDamage = 0;
  private _rootApplied = false;
  private _drApplied = false;
  private _liquidizeApplied = false;
  private _holdElapsedMs = 0;

  constructor(deps: CrushDeps, config: Partial<CrushConfig> = {}) {
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- State getters ---

  get phase(): CrushPhase { return this._phase; }
  get holdActive(): boolean { return this._holdActive; }
  get movementLocked(): boolean { return this._movementLocked; }
  get resolvedRange(): number { return this._resolvedRange; }
  get resolvedDamageRate(): number { return this._resolvedDamageRate; }
  get ticksFired(): number { return this._ticksFired; }
  get damageTicks(): readonly number[] { return this._damageTicks; }
  get totalDamage(): number { return this._totalDamage; }
  get rootApplied(): boolean { return this._rootApplied; }
  get drApplied(): boolean { return this._drApplied; }
  get liquidizeApplied(): boolean { return this._liquidizeApplied; }
  get holdElapsedMs(): number { return this._holdElapsedMs; }
  get fizzled(): boolean { return this._fizzled; }

  get cooldown(): number {
    return this._phase === 'cooldown' ? this._phaseTimer : 0;
  }

  // --- SkillEffect interface ---

  canActivate(): boolean {
    if (this._phase !== 'idle') return false;
    const bonusRange = this.deps.resolver.getAdditiveEffect('crush', 'rangeTiles');
    this._resolvedRange = this.config.baseRangeTiles + bonusRange;
    return this.deps.targeting.hasTargetInRange(this._resolvedRange);
  }

  activate(): boolean {
    if (!this.canActivate()) return false;

    this._complete = false;
    this._holdActive = false;
    this._movementLocked = false;
    this._rootApplied = false;
    this._drApplied = false;
    this._liquidizeApplied = false;
    this._holdElapsedMs = 0;
    this._tickTimer = 0;
    this._ticksFired = 0;
    this._damageTicks = [];
    this._totalDamage = 0;
    this._resolvedDamageRate = 0;
    this._fizzled = this.deps.targeting.shouldFizzle();

    // Enter windup
    this._phase = 'windup';
    this._phaseTimer = this.config.windupMs;

    return true;
  }

  deactivate(): void {
    if (this._phase === 'active') {
      this.endHold();
    }
  }

  isActive(): boolean {
    return this._phase === 'windup' || this._phase === 'active' || this._phase === 'recovery';
  }

  isComplete(): boolean {
    return this._complete;
  }

  interrupt(): void {
    if (this._phase === 'idle' || this._phase === 'cooldown') return;
    this.removeDr();
    this._holdActive = false;
    this._movementLocked = false;
    this._phase = 'idle';
    this._phaseTimer = 0;
    this._complete = true;
  }

  update(delta: number): void {
    if (this._phase === 'idle') return;

    // During hold: track elapsed time, fire damage ticks, check target liveness
    if (this._phase === 'active') {
      this._holdElapsedMs += delta;

      // Check target liveness before ticking
      if (!this.deps.targeting.isTargetAlive()) {
        this.endHold();
        return;
      }

      // Accumulate tick timer and fire damage ticks
      this._tickTimer += delta;
      if (this._tickTimer >= this.config.tickIntervalMs) {
        this._tickTimer -= this.config.tickIntervalMs;
        this.fireDamageTick();
      }
    }

    this._phaseTimer -= delta;
    if (this._phaseTimer <= 0) {
      this.advancePhase();
    }
  }

  destroy(): void {
    this.removeDr();
    this._phase = 'idle';
    this._phaseTimer = 0;
    this._holdActive = false;
    this._movementLocked = false;
  }

  // --- Internal ---

  private advancePhase(): void {
    switch (this._phase) {
      case 'windup':
        if (this._fizzled) {
          this.enterRecovery();
        } else {
          this.enterHold();
        }
        break;
      case 'active':
        this.endHold();
        break;
      case 'recovery':
        this._phase = 'cooldown';
        this._phaseTimer = this.config.cooldownMs;
        this._complete = true;
        break;
      case 'cooldown':
        this._phase = 'idle';
        this._phaseTimer = 0;
        break;
    }
  }

  private enterHold(): void {
    this._phase = 'active';
    this._phaseTimer = this.config.holdDurationMs;
    this._holdActive = true;
    this._movementLocked = true;

    // Resolve damage rate: Cinched upgrade or base
    if (this.deps.resolver.hasEffect('crush', 'crushDamagePerSecond')) {
      this._resolvedDamageRate = this.deps.resolver.getMaxEffect('crush', 'crushDamagePerSecond') ?? this.config.baseDamagePerSecond;
    } else {
      this._resolvedDamageRate = this.config.baseDamagePerSecond;
    }

    // Apply crush_hold status to target
    this.deps.statusEffects.apply('crush_hold', this.config.holdDurationMs, 'crush');
    this._rootApplied = true;

    // Clinch passive: DR during any grapple-path hold
    if (this.deps.resolver.hasEffect('grapple', 'grappleDamageReduction')) {
      this.deps.statusEffects.apply('grit_force_bind_dr', this.config.holdDurationMs, this.config.damageReduction, 'crush');
      this._drApplied = true;
    }
  }

  private fireDamageTick(): void {
    const targetMaxHp = this.deps.targeting.getTargetMaxHp();
    const damage = targetMaxHp * this._resolvedDamageRate;
    this._damageTicks.push(damage);
    this._totalDamage += damage;
    this._ticksFired++;
  }

  private endHold(): void {
    this.removeDr();
    this.applyLiquidize();
    this._holdActive = false;
    this._movementLocked = false;
    this.enterRecovery();
  }

  private enterRecovery(): void {
    this._phase = 'recovery';
    this._phaseTimer = this.config.recoveryMs;
  }

  private applyLiquidize(): void {
    if (this.deps.resolver.hasEffect('crush', 'postCrushArmorReduction')) {
      const intensity = this.deps.resolver.getAdditiveEffect('crush', 'postCrushArmorReduction');
      const durationMs = this.deps.resolver.getMaxEffect('crush', 'armorReductionDurationMs') ?? 4000;
      this.deps.statusEffects.apply('liquidize_debuff', durationMs, intensity, 'crush');
      this._liquidizeApplied = true;
    }
  }

  private removeDr(): void {
    if (this._drApplied) {
      this.deps.statusEffects.remove('grit_force_bind_dr', 'crush');
      this._drApplied = false;
    }
  }
}
